import { useQueryClient } from '@tanstack/react-query';
import { MdAdd } from 'react-icons/md';
import React, { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { UiPen, UiLinkAdd } from '@vyaguta/vyaguta-icons/ui';

import LFModal from 'components/common/LFModal/LFModal';
import { QUICK_LINKS_QUERY_KEY } from 'hooks/query/useQuickLinksQuery';

import { deleteQuickLinksCategory } from 'services/quickLinks';

import AddLink from 'pages/eventsAndNotices/linkLibrary/components/AddLink';
import AddCategory from 'pages/eventsAndNotices/linkLibrary/components/AddCategory';
import DeletePopup from 'pages/eventsAndNotices/linkLibrary/components/DeletePopup';

import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import { LinkLibraryNode } from 'interface/linkLibraryTree';

type LinkLibraryCategoryNode = LinkLibraryNode & { id: number | string };

interface Props {
  selectedCategory: LinkLibraryCategoryNode;
  popoverId?: string;
  setActivePopoverId?: React.Dispatch<React.SetStateAction<string | null>>;
}

const SubCategoryVerticalEllipsis: React.FC<Props> = (props) => {
  const { selectedCategory, popoverId, setActivePopoverId } = props;
  const queryClient = useQueryClient();

  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [isCategoryEditModalOpen, setIsCategoryEditModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const closeCategoryEditModal = () => {
    setIsCategoryEditModalOpen(false);
  };

  const closeSubCategoryModal = () => {
    setIsSubCategoryModalOpen(false);
  };

  const closeLinkModal = () => {
    setIsLinkModalOpen(false);
  };

  const closePopover = () => {
    if (!setActivePopoverId || !popoverId) {
      return;
    }

    setActivePopoverId((current) => (current === popoverId ? null : current));
  };

  const EditCategoryPopUp: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
      <li
        className="overflow-menu__list-item overflow-menu__list-item--hoverable d-flex"
        onClick={() => {
          closePopover();
          setIsCategoryEditModalOpen(true);
          onClick();
        }}
      >
        <span className="icon overflow-menu__icon">
          <UiPen className="grey--text" size={16} />
        </span>
        <span>Edit</span>
      </li>
    );
  };

  const AddLinkPopup: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
      <li
        className="overflow-menu__list-item overflow-menu__list-item--hoverable d-flex"
        onClick={() => {
          closePopover();
          setIsLinkModalOpen(true);
          onClick();
        }}
      >
        <span className="icon overflow-menu__icon">
          <UiLinkAdd className="grey--text" size={16} />
        </span>
        <span>Add new link</span>
      </li>
    );
  };

  const AddLinkSubCategory: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
      <li
        className="overflow-menu__list-item overflow-menu__list-item--hoverable d-flex"
        onClick={() => {
          closePopover();
          setIsSubCategoryModalOpen(true);
          onClick();
        }}
      >
        <span className="icon overflow-menu__icon">
          <MdAdd className="grey--text" size={16} />
        </span>
        <span>Add new sub-category</span>
      </li>
    );
  };

  const DeleteSubCategoryTrigger: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
      <DeletePopup
        onClick={(e) => {
          e.preventDefault();
          closePopover();
          onClick();
        }}
      />
    );
  };

  const deleteCategory = async () => {
    try {
      const data = (await deleteQuickLinksCategory(selectedCategory.id)) as { message: string };

      toast.success({
        title: 'Success',
        message: data.message,
      });

      queryClient.invalidateQueries({ queryKey: QUICK_LINKS_QUERY_KEY });
      // await setHierarchyData(selectedCategory.parentId);
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="overflow-menu">
      <div className="overflow-menu__block">
        <ul className="overflow-menu__list">
          <AddCategory
            isEdit={true}
            key="editSubCategory"
            isOpen={isCategoryEditModalOpen}
            onClose={closeCategoryEditModal}
            isSubCategory={true}
            selectedCategory={selectedCategory}
            TriggerBtn={EditCategoryPopUp}
          />
          <AddCategory
            isSubCategory={true}
            key="addCategory"
            isEdit={false}
            isOpen={isSubCategoryModalOpen}
            onClose={closeSubCategoryModal}
            selectedCategory={selectedCategory}
            TriggerBtn={AddLinkSubCategory}
          />
          <AddLink
            isEdit={false}
            isOpen={isLinkModalOpen}
            onClose={closeLinkModal}
            selectedLink={selectedCategory}
            TriggerBtn={AddLinkPopup}
          />
          <LFModal
            TriggerBtn={DeleteSubCategoryTrigger}
            onClose={closePopover}
            modalStatusIcon={<FiAlertTriangle color="#F44336" size={24} />}
            title={{ text: `Delete category ${selectedCategory.name}`, type: 'danger' }}
            className="delete-modal"
            htmlContent={
              <div className="d-grid mbpx-4">
                <span className="delete__text">
                  This action cannot be reverted back. The sub-category {selectedCategory.name} will be deleted with the
                  links or category under it.
                </span>
              </div>
            }
            renderFooter={true}
            buttons={[
              {
                text: 'Yes, delete',
                type: 'red',
                className: 'mr-12 delete__btn',
                onClick: deleteCategory,
                close: true,
                showLoading: true,
              },
              {
                text: 'No, go back',
                type: 'outlined-grey',
                className: 'py-8 px-12',
                close: true,
              },
            ]}
          />
        </ul>
      </div>
    </div>
  );
};

export default SubCategoryVerticalEllipsis;
